import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { DriverPeriodFilter } from "components/DriverPeriodFilter";
import { DriverCard, DriverListSkeleton, EmptyState } from "components/DriverCard";
import { PageTitle } from "components/PageTitle";
import { driverDashboardService } from "services/driverDashboardService";
import { IDriverDeliveries, IDriverDeliveryAttempt } from "interfaces/IDriverDashboard";
import {
  DeliveryBadges,
  DeliveryDetailGrid,
  DeliveryHeader,
  DeliveryItem,
  DeliveryMeta,
  DeliveryPrimaryInfo,
  DeliveryStatus,
  DetailItem,
  OrderFlag,
} from "./styles";

const TODAY = dayjs().format("YYYY-MM-DD");

type DeliveryWindow = {
  initial?: unknown;
  final?: unknown;
};

function getDeliveryStatus(delivered: boolean | null) {
  if (delivered === true) {
    return { label: "Entregue", kind: "success" as const };
  }

  if (delivered === false) {
    return { label: "Não entregue", kind: "failed" as const };
  }

  return { label: "Pendente", kind: "pending" as const };
}

function formatWindowTime(value: unknown) {
  if (!value || typeof value !== "string") {
    return null;
  }

  if (/^\d{2}:\d{2}/.test(value)) {
    return value.slice(0, 5);
  }

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("HH:mm") : null;
}

function getDeliveryWindow(delivery: IDriverDeliveryAttempt) {
  const window = delivery.deliveryWindow as DeliveryWindow | null;
  const start = formatWindowTime(window?.initial);
  const end = formatWindowTime(window?.final);

  return {
    start,
    end,
    label: start && end ? `${start} - ${end}` : end ?? start ?? "Sem janela",
  };
}

export function DriverDeliveries() {
  const [startDate, setStartDate] = useState(dayjs().subtract(7, "day").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(TODAY);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<IDriverDeliveries | null>(null);

  useEffect(() => {
    setLoading(true);
    driverDashboardService
      .getMyDeliveries({ startDate, endDate })
      .then(setData)
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  return (
    <div aria-busy={loading}>
      <PageTitle>Entregas</PageTitle>

      <DriverPeriodFilter
        startDate={startDate}
        endDate={endDate}
        onChange={(start, end) => {
          setStartDate(start);
          setEndDate(end);
        }}
        disabled={loading}
      />

      {loading ? (
        <DriverListSkeleton cards={3} rows={4} />
      ) : (
        <>
          {(data?.results?.length ?? 0) === 0 && (
            <EmptyState>Nenhuma entrega no período selecionado.</EmptyState>
          )}
          {data?.results?.map((day) => (
            <DriverCard
              key={`${day.date}-${day.route ?? "sem-rota"}`}
              title={`${dayjs(day.date).format("DD/MM/YYYY")} ${day.route ? `- Rota ${day.route}` : ""}`}
            >
              {day.deliveries.map((delivery) => {
                const status = getDeliveryStatus(delivery.successfulDelivery);
                const deliveryWindow = getDeliveryWindow(delivery);

                return (
                  <DeliveryItem key={delivery.id}>
                    <DeliveryHeader>
                      <DeliveryPrimaryInfo>
                        <strong>Pedido {delivery.orderId}</strong>
                        <DeliveryMeta>
                          {delivery.routeTag?.attendanceOrder && (
                            <span>Sequência {delivery.routeTag.attendanceOrder}</span>
                          )}
                          {delivery.routeTag?.region && <span>{delivery.routeTag.region}</span>}
                        </DeliveryMeta>
                      </DeliveryPrimaryInfo>
                      <DeliveryStatus kind={status.kind}>{status.label}</DeliveryStatus>
                    </DeliveryHeader>

                    <DeliveryDetailGrid>
                      <DetailItem>
                        <span>Janela prevista</span>
                        <strong>{deliveryWindow.label}</strong>
                      </DetailItem>
                      <DetailItem highlight>
                        <span>Entregar até</span>
                        <strong>{deliveryWindow.end ?? "-"}</strong>
                      </DetailItem>
                      <DetailItem>
                        <span>Chegada</span>
                        <strong>{delivery.attemptTime ? dayjs(delivery.attemptTime).format("HH:mm") : "-"}</strong>
                      </DetailItem>
                      <DetailItem>
                        <span>Caixas</span>
                        <strong>{delivery.boxes ?? "-"}</strong>
                      </DetailItem>
                    </DeliveryDetailGrid>

                    {(delivery.isFirstOrder || delivery.previousOrderHadComplaint) && (
                      <DeliveryBadges>
                        {delivery.isFirstOrder && <OrderFlag kind="first">Primeiro pedido</OrderFlag>}
                        {delivery.previousOrderHadComplaint && (
                          <OrderFlag kind="complaint">Último pedido com quebra</OrderFlag>
                        )}
                      </DeliveryBadges>
                    )}
                  </DeliveryItem>
                );
              })}
            </DriverCard>
          ))}
        </>
      )}
    </div>
  );
}

export default DriverDeliveries;
